/**
 * TourEditor.jsx
 * Admin panel section for editing tour step content without redeploy.
 * Steps are fetched from / saved to DB via PUT /api/market/admin/tour-content.
 * Falls back to the hardcoded defaults in TourWizard.jsx if DB is empty.
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Save, RotateCcw, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { STEPS as DEFAULT_STEPS } from "./TourWizard";
import { toast } from "sonner";

const HUBS    = ["carrier", "recruiter", "safety", "driver", "admin"];
const LABELS  = { carrier: "Carrier", recruiter: "Recruiter", safety: "Safety", driver: "Driver", admin: "Admin" };
const ACCENT  = "#FF4D00";
const GOLD    = "#F5C842";

function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }

export function TourEditor({ token }) {
  const [content, setContent] = useState({});   // { hub: [{title, body}, …] }
  const [saving, setSaving]   = useState(false);
  const [saved,  setSaved]    = useState(false);
  const [openHub, setOpenHub] = useState("carrier");

  // Load current DB content (merge with defaults)
  useEffect(() => {
    api.marketGetTourContent()
      .then((data) => {
        const dbContent = data?.tour_content || {};
        const merged = {};
        HUBS.forEach((hub) => {
          const defaults = DEFAULT_STEPS[hub] || [];
          const overrides = dbContent[hub] || [];
          merged[hub] = defaults.map((s, i) => ({
            title: overrides[i]?.title || s.title,
            body:  overrides[i]?.body  || s.body,
          }));
        });
        setContent(merged);
      })
      .catch(() => {
        // Init from defaults
        const init = {};
        HUBS.forEach((hub) => {
          init[hub] = (DEFAULT_STEPS[hub] || []).map((s) => ({ title: s.title, body: s.body }));
        });
        setContent(init);
      });
  }, []);

  function updateStep(hub, idx, field, val) {
    setContent((prev) => {
      const next = deepClone(prev);
      if (!next[hub]) next[hub] = [];
      if (!next[hub][idx]) next[hub][idx] = {};
      next[hub][idx][field] = val;
      return next;
    });
    setSaved(false);
  }

  function resetHub(hub) {
    setContent((prev) => ({
      ...prev,
      [hub]: (DEFAULT_STEPS[hub] || []).map((s) => ({ title: s.title, body: s.body })),
    }));
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    try {
      await api.marketSaveTourContent(token, content);
      setSaved(true);
      toast.success("Tour content saved — changes are live immediately.");
    } catch {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const inputBase = {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "#fff",
    outline: "none",
    width: "100%",
    fontFamily: "inherit",
    fontSize: 13,
    resize: "vertical",
  };

  return (
    <div data-testid="tour-editor-panel" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-head text-sm font-black uppercase tracking-[0.15em] text-white">
            Tour Content Editor
          </h3>
          <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.35)" }}>
            Edit the onboarding tour text for each hub. Changes go live immediately — no redeploy needed.
          </p>
        </div>
        <button
          data-testid="tour-editor-save-btn"
          onClick={save}
          disabled={saving || saved}
          className="flex items-center gap-2 font-head text-[10px] uppercase tracking-[0.2em] px-4 py-2 transition-all hover:brightness-110 disabled:opacity-60"
          style={{ background: saved ? "rgba(74,222,128,0.15)" : GOLD, color: saved ? "#4ade80" : "#111",
            border: saved ? "1px solid rgba(74,222,128,0.3)" : "none" }}
        >
          {saved ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "Saving…" : saved ? "Saved" : "Save All Changes"}
        </button>
      </div>

      {/* Hub accordion */}
      {HUBS.map((hub) => {
        const steps = content[hub] || [];
        const isOpen = openHub === hub;
        return (
          <div key={hub} style={{ border: "1px solid rgba(255,255,255,0.08)", background: "#0a0a0e" }}>
            {/* Hub header */}
            <button
              data-testid={`tour-editor-hub-${hub}`}
              onClick={() => setOpenHub(isOpen ? null : hub)}
              className="w-full flex items-center justify-between px-4 py-3 transition-colors hover:bg-white/[0.03]"
            >
              <span className="font-head text-xs font-black uppercase tracking-[0.15em] text-white">
                {LABELS[hub]} Hub — {steps.length} steps
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); resetHub(hub); }}
                  title="Reset to defaults"
                  className="flex items-center gap-1 font-head text-[8px] uppercase tracking-[0.2em] transition-opacity hover:opacity-70"
                  style={{ color: "rgba(255,77,0,0.6)" }}
                >
                  <RotateCcw className="w-2.5 h-2.5" /> Reset
                </button>
                {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-white/30" /> : <ChevronDown className="w-3.5 h-3.5 text-white/30" />}
              </div>
            </button>

            {/* Steps */}
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="px-4 pb-4 space-y-4"
                style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
              >
                {steps.map((s, idx) => (
                  <div key={idx} className="pt-3">
                    <p className="font-head text-[9px] uppercase tracking-[0.2em] mb-2"
                      style={{ color: "rgba(255,255,255,0.25)" }}>
                      Step {idx + 1}
                    </p>
                    {/* Title */}
                    <input
                      data-testid={`tour-step-title-${hub}-${idx}`}
                      value={s.title}
                      onChange={(e) => updateStep(hub, idx, "title", e.target.value)}
                      placeholder="Step title"
                      style={{ ...inputBase, padding: "8px 10px", marginBottom: 6 }}
                    />
                    {/* Body */}
                    <textarea
                      data-testid={`tour-step-body-${hub}-${idx}`}
                      value={s.body}
                      rows={3}
                      onChange={(e) => updateStep(hub, idx, "body", e.target.value)}
                      placeholder="Step description"
                      style={{ ...inputBase, padding: "8px 10px" }}
                    />
                  </div>
                ))}
              </motion.div>
            )}
          </div>
        );
      })}
    </div>
  );
}
